import React, { useState } from 'react';
import { Download, Check, Share, Plus, X, Smartphone } from 'lucide-react';
import { usePwaInstall } from '../hooks/usePwaInstall';
import { toast } from 'sonner';

/**
 * Installable "Install app" button. Renders nothing if already installed.
 * Variants:
 *   compact  — icon-only pill (navbar)
 *   default  — icon + label pill
 *   large    — big CTA button (landing / tour finish)
 *   ghost    — low-emphasis secondary button
 */
export default function PwaInstallButton({ variant = 'default', label, className = '', testid = 'pwa-install-button' }) {
  const { canInstall, installed, iosHint, install } = usePwaInstall();
  const [showIos, setShowIos] = useState(false);

  if (installed) return null;
  // Hide entirely if neither Chromium prompt nor iOS hint is available — nothing to offer.
  if (!canInstall) return null;

  const click = async () => {
    const r = await install();
    if (r === 'ios') setShowIos(true);
    else if (r === 'accepted') toast.success('Installed — launch ReaperCast from your home screen.');
    else if (r === 'unsupported') toast.info('This browser doesn\'t support one-tap install. Use your browser\'s menu to add this site to your home screen.');
  };

  const base = {
    compact: 'w-9 h-9 md:w-auto md:h-auto md:px-3 md:py-1.5 rounded-full border border-white/10 bg-white/[0.05] hover:bg-white/[0.1] text-[12.5px] font-semibold flex items-center justify-center gap-1.5',
    default: 'px-3 py-2 rounded-full border border-white/10 bg-white/[0.05] hover:bg-white/[0.1] text-[12.5px] font-semibold flex items-center gap-1.5',
    large:   'px-5 py-3 rounded-full text-white font-semibold text-[14px] inline-flex items-center gap-2',
    ghost:   'px-3 py-2 rounded-full text-white/80 hover:text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5',
  }[variant] || 'btn-ghost';

  const style = variant === 'large'
    ? { background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' }
    : undefined;

  const displayLabel = label || (variant === 'compact' ? null : (iosHint ? 'Install app' : 'Install'));

  return (
    <>
      <button onClick={click} className={`${base} ${className}`} style={style} data-testid={testid} aria-label="Install ReaperCast as app">
        <Download className="w-4 h-4" />
        {displayLabel && (
          <span className={variant === 'compact' ? 'hidden md:inline' : ''}>{displayLabel}</span>
        )}
      </button>
      {showIos && <IosHintSheet onClose={() => setShowIos(false)} />}
    </>
  );
}

function IosHintSheet({ onClose }) {
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm grid place-items-end md:place-items-center p-0 md:p-6" onClick={onClose} data-testid="pwa-ios-hint">
      <div className="bg-[#141418] border-t md:border border-white/10 w-full md:max-w-[440px] md:rounded-2xl rounded-t-2xl p-6 md:p-7 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 p-1 text-white/60 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        <div className="w-12 h-12 rounded-xl grid place-items-center mb-3" style={{ background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' }}>
          <Smartphone className="w-6 h-6 text-black" />
        </div>
        <h3 className="font-display text-[22px] leading-tight">Install on iPhone / iPad</h3>
        <p className="text-white/65 text-[13.5px] mt-2 leading-relaxed">Safari doesn't have a one-tap install, but it takes 3 seconds:</p>
        <ol className="mt-4 space-y-3 text-[13.5px]">
          <li className="flex items-start gap-3"><span className="w-7 h-7 shrink-0 rounded-full grid place-items-center bg-white/10 border border-white/15 font-bold text-[12px]">1</span><span>Tap the <b>Share</b> button <Share className="w-3.5 h-3.5 inline mx-1" /> at the bottom of Safari.</span></li>
          <li className="flex items-start gap-3"><span className="w-7 h-7 shrink-0 rounded-full grid place-items-center bg-white/10 border border-white/15 font-bold text-[12px]">2</span><span>Scroll and tap <b>Add to Home Screen</b> <Plus className="w-3.5 h-3.5 inline mx-1" />.</span></li>
          <li className="flex items-start gap-3"><span className="w-7 h-7 shrink-0 rounded-full grid place-items-center bg-white/10 border border-white/15 font-bold text-[12px]">3</span><span>Tap <b>Add</b>. ReaperCast opens like a native app — full-screen, works offline.</span></li>
        </ol>
        <button onClick={onClose} className="btn-primary w-full justify-center mt-6" data-testid="pwa-ios-hint-close"><Check className="w-4 h-4" /> Got it</button>
      </div>
    </div>
  );
}
